/**
 * The inline picture editor: the stage takes the picture's place inside the platform frame and
 * the controls sit in the right column. Everything goes through the browser renderer, so what
 * the host sees is what gets delivered.
 */
import { useState, type Dispatch, type SetStateAction } from 'react';
import Cropper from 'react-easy-crop';
import { CANVAS_SIZE, type CardEdit, type Canvas } from '@str/shared';
import { draftEdit, type PictureDraft } from '@/lib/picture-draft';
import { remoteImage, sourceUrl } from '@/lib/composer-cards';
import { AUTO_FIX, LOOKS, lookOf } from '@/lib/composer-v2';
import { corsSource } from '@/lib/cors-source';
import { PictureStage, type PictureTool } from '@/components/composer/PictureStage';
import { PictureTools } from '@/components/composer/PictureTools';

export type EditorTool = 'Crop' | 'Filters' | 'Adjust' | 'Text' | 'Sticker';

const STAGE_TOOL: Record<Exclude<EditorTool, 'Crop'>, PictureTool> = {
  Filters: 'Adjust',
  Adjust: 'Adjust',
  Text: 'Text',
  Sticker: 'Sticker',
};

export function EditorStage({
  draft,
  tool,
  onDraft,
  busy,
}: {
  draft: PictureDraft;
  tool: EditorTool;
  onDraft: Dispatch<SetStateAction<PictureDraft>>;
  busy: boolean;
}) {
  const [retry, setRetry] = useState(0);
  const size = CANVAS_SIZE[draft.shape];
  const url = sourceUrl(draft.edit.source);
  const edit = draftEdit(draft);
  return (
    <div
      className="nx-editor-stage"
      style={{ aspectRatio: `${size.width} / ${size.height}` }}
      data-testid="editor-stage"
    >
      {tool === 'Crop' ? (
        <div className="composer-crop-stage">
          <Cropper
            key={`${draft.shape}-${retry}`}
            image={corsSource(url)}
            crop={draft.position}
            zoom={draft.zoom}
            aspect={size.width / size.height}
            onCropChange={(position) => onDraft((current) => ({ ...current, position }))}
            onZoomChange={(zoom) => onDraft((current) => ({ ...current, zoom }))}
            onCropComplete={(area) => onDraft((current) => ({ ...current, area }))}
            style={{
              cropAreaStyle: {
                border: '2px solid #fff',
                outline: '2px solid #111',
                boxShadow: '0 0 0 9999em rgba(0, 0, 0, .65)',
              },
            }}
            maxZoom={10}
            initialCroppedAreaPercentages={
              draft.edit.crop && !draft.area
                ? {
                    x: draft.edit.crop.x * 100,
                    y: draft.edit.crop.y * 100,
                    width: draft.edit.crop.w * 100,
                    height: draft.edit.crop.h * 100,
                  }
                : undefined
            }
            mediaProps={{
              ...(remoteImage(url) ? { crossOrigin: 'anonymous' } : {}),
              onError: () => setRetry((n) => n + 1),
            }}
          />
        </div>
      ) : (
        <PictureStage
          edit={edit}
          onChange={(next) =>
            onDraft((current) => ({
              ...current,
              edit: { ...current.edit, layers: next.layers, adjust: next.adjust },
            }))
          }
          tool={STAGE_TOOL[tool]}
          selected={draft.selected}
          onSelect={(selected) => onDraft((current) => ({ ...current, selected }))}
          editing={draft.editing}
          onEditing={(editing) => onDraft((current) => ({ ...current, editing }))}
          color={draft.color}
          strokeWidth={draft.strokeWidth}
          disabled={busy}
        />
      )}
    </div>
  );
}

export function EditorControls({
  draft,
  tool,
  onDraft,
  busy,
  headline,
  count,
  lockedShape,
  thumbnail,
}: {
  draft: PictureDraft;
  tool: EditorTool;
  onDraft: Dispatch<SetStateAction<PictureDraft>>;
  busy: boolean;
  headline: string;
  count: number;
  lockedShape: boolean;
  /** Background style for the filter swatches. */
  thumbnail: React.CSSProperties;
}) {
  const edit = draftEdit(draft);
  const setAdjust = (adjust: CardEdit['adjust']) =>
    onDraft((current) => ({ ...current, edit: { ...current.edit, adjust } }));
  if (tool === 'Crop')
    return (
      <>
        <p className="nx-note">
          Drag the photo to position it. Only the area inside the frame is kept.
        </p>
        {lockedShape ? (
          <p className="nx-note">Reels and stories are always vertical (9:16).</p>
        ) : (
          <div className="nx-row" style={{ cursor: 'default' }}>
            <span className="k">Shape</span>
            <div className="seg round" role="radiogroup" aria-label="Crop shape">
              {(['4:5', '1:1', '1.91:1'] as Canvas[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  role="radio"
                  aria-checked={draft.shape === s}
                  className={`seg-item hv${draft.shape === s ? ' on' : ''}`}
                  disabled={busy}
                  onClick={() =>
                    draft.shape !== s &&
                    onDraft((current) => ({
                      ...current,
                      shape: s,
                      area: null,
                      position: { x: 0, y: 0 },
                      zoom: 1,
                    }))
                  }
                >
                  {s === '4:5' ? 'Portrait' : s === '1:1' ? 'Square' : 'Landscape'}
                </button>
              ))}
            </div>
          </div>
        )}
        {count > 1 && (
          <p className="nx-note">
            One shape for the whole post, the way Instagram works. Changing it recentres every
            picture.
          </p>
        )}
        <div className="nx-slider">
          <span>Zoom</span>
          <input
            type="range"
            min="1"
            max="10"
            step=".01"
            value={draft.zoom}
            aria-label="Zoom"
            disabled={busy}
            onChange={(e) => onDraft((current) => ({ ...current, zoom: Number(e.target.value) }))}
          />
          <span className="n">{draft.zoom.toFixed(1)}×</span>
        </div>
      </>
    );
  if (tool === 'Filters') {
    const current = lookOf(edit.adjust);
    return (
      <>
        <div className="nx-looks" role="radiogroup" aria-label="Filters">
          {LOOKS.map((l) => (
            <button
              key={l.key}
              type="button"
              role="radio"
              aria-checked={current === l.key}
              className={`nx-look${current === l.key ? ' on' : ''}`}
              disabled={busy}
              onClick={() => setAdjust({ ...l.adjust })}
            >
              <span style={{ ...thumbnail, filter: cssFilter(l.adjust) }} />
              {l.label}
            </button>
          ))}
        </div>
        {count > 1 && applyAll(draft, onDraft, count)}
        <p className="nx-note keep">Your photo stays your photo.</p>
      </>
    );
  }
  if (tool === 'Adjust')
    return (
      <>
        <PictureTools
          tool="Adjust"
          edit={edit}
          onChange={(next) => setAdjust(next.adjust)}
          selected={draft.selected}
          onSelect={(selected) => onDraft((current) => ({ ...current, selected }))}
          editing={draft.editing}
          onEditing={(editing) => onDraft((current) => ({ ...current, editing }))}
          headline={headline}
          color={draft.color}
          onColor={(color) => onDraft((current) => ({ ...current, color }))}
          strokeWidth={draft.strokeWidth}
          onWidth={(strokeWidth) => onDraft((current) => ({ ...current, strokeWidth }))}
          disabled={busy}
        />
        <div className="nx-chips">
          <button
            type="button"
            className="nx-chip ai"
            disabled={busy}
            onClick={() => setAdjust({ ...AUTO_FIX })}
          >
            ✦ Auto-fix
          </button>
          <button
            type="button"
            className="nx-chip"
            disabled={busy}
            onClick={() => setAdjust({ brightness: 0, contrast: 0, saturation: 0, warmth: 0 })}
          >
            Reset
          </button>
        </div>
        {count > 1 && applyAll(draft, onDraft, count)}
        <p className="nx-note keep">Your photo stays your photo.</p>
      </>
    );
  return (
    <>
      <PictureTools
        tool={tool}
        edit={edit}
        onChange={(next) =>
          onDraft((current) => ({
            ...current,
            edit: { ...current.edit, layers: next.layers, adjust: next.adjust },
          }))
        }
        selected={draft.selected}
        onSelect={(selected) => onDraft((current) => ({ ...current, selected }))}
        editing={draft.editing}
        onEditing={(editing) => onDraft((current) => ({ ...current, editing }))}
        headline={headline}
        color={draft.color}
        onColor={(color) => onDraft((current) => ({ ...current, color }))}
        strokeWidth={draft.strokeWidth}
        onWidth={(strokeWidth) => onDraft((current) => ({ ...current, strokeWidth }))}
        disabled={busy}
      />
      <p className="nx-note">Drag it on the picture to place it. Double-click text to change it.</p>
    </>
  );
}

function applyAll(
  draft: PictureDraft,
  onDraft: Dispatch<SetStateAction<PictureDraft>>,
  count: number,
) {
  return (
    <div className="nx-toggle">
      <button
        type="button"
        className={`nx-switch${draft.applyToAll ? ' on' : ''}`}
        aria-label={`Apply to all ${count}`}
        onClick={() => onDraft((current) => ({ ...current, applyToAll: !draft.applyToAll }))}
      />
      <span>Apply to all {count} pictures</span>
    </div>
  );
}

/** A CSS approximation of the renderer's adjustments, for swatches only. */
function cssFilter(a: CardEdit['adjust']): string {
  const warm = a.warmth >= 0 ? `sepia(${a.warmth * 0.6})` : `hue-rotate(${a.warmth * 25}deg)`;
  return `brightness(${1 + a.brightness}) contrast(${1 + a.contrast}) saturate(${1 + a.saturation}) ${warm}`;
}
